import React, { useState, useCallback } from 'react';
import { Check, ChevronRight, ChevronLeft, User, Camera, FileText, Mail, Star, Loader2 } from 'lucide-react';
import { PhotoUploader } from './PhotoUploader';
import { useToast } from '../../../hooks/useToast';
import { useSubmitDeliveryCompleteMutation } from '../../../store/slices/apiSlice';

interface CompleteDeliveryFlowProps {
  jobId: string;
  onComplete: () => void;
  onCancel: () => void;
  existingData?: DeliveryRecord | null;
}

interface DeliveryRecord {
  status: 'delivered' | 'incomplete';
  receiver_name?: string;
  photos: string[];
  notes: string;
}

export const CompleteDeliveryFlow: React.FC<CompleteDeliveryFlowProps> = ({
  jobId,
  onComplete,
  onCancel,
  existingData,
}) => {
  const [step, setStep] = useState(1);
  const [receiverName, setReceiverName] = useState(existingData?.receiver_name || '');
  const [photos, setPhotos] = useState<string[]>(existingData?.photos || []);
  const [notes, setNotes] = useState(existingData?.notes || '');
  const [doNotSendReview, setDoNotSendReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const toast = useToast();

  const [submitComplete] = useSubmitDeliveryCompleteMutation();

  const validateStep = useCallback((currentStep: number): boolean => {
    switch (currentStep) {
      case 1:
        if (receiverName.length < 2) {
          toast.error('Please enter a valid name', 'Receiver name must be at least 2 characters');
          return false;
        }
        return true;
      case 2:
        if (photos.length === 0) {
          toast.error('Photos required', 'Please add at least one photo');
          return false;
        }
        return true;
      case 3:
        if (notes.length < 5) {
          toast.error('Notes too short', 'Please provide more details (min 5 characters)');
          return false;
        }
        return true;
      case 4:
        return true;
      default:
        return true;
    }
  }, [receiverName, photos, notes, toast]);

  const handleNext = useCallback(() => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  }, [step, validateStep]);

  const handleBack = useCallback(() => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      onCancel();
    }
  }, [step, onCancel]);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(4)) return;

    setSubmitting(true);
    
    try {
      const payload = {
        receiver_name: receiverName,
        photos,
        notes,
        send_review: !doNotSendReview,
      };

      // Always use submitComplete - sends email for both first-time and edit (same as first time)
      await submitComplete({ jobId, data: payload }).unwrap();
      toast.success('Success', existingData?.status === 'delivered' ? 'Changes saved and email sent' : 'Delivery marked as complete');
      
      onComplete();
    } catch (error: any) {
      const message = error?.data?.detail || error?.data?.error || 'Please try again';
      toast.error('Failed to save', message);
    } finally {
      setSubmitting(false);
    }
  }, [validateStep, jobId, receiverName, photos, notes, doNotSendReview, existingData, submitComplete, onComplete, toast]);

  // Progress Bar
  const renderProgressBar = () => (
    <div className="flex items-center gap-2 mb-6">
      {[1, 2, 3, 4].map((s) => (
        <React.Fragment key={s}>
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all
            ${s === step ? 'bg-blue-600 text-white ring-4 ring-blue-100' : ''}
            ${s < step ? 'bg-green-500 text-white' : ''}
            ${s > step ? 'bg-gray-200 text-gray-500' : ''}
          `}>
            {s < step ? <Check className="w-5 h-5" /> : s}
          </div>
          {s < 4 && (
            <div className={`
              flex-1 h-1.5 rounded-full transition-all
              ${s < step ? 'bg-green-500' : 'bg-gray-200'}
            `} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step 1: Receiver Name
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <User className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Who received the delivery?</h3>
        <p className="text-gray-500 text-sm mt-1">Enter the name of the person who accepted the delivery</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Receiver Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={receiverName}
          onChange={(e) => setReceiverName(e.target.value)}
          placeholder="e.g., John Smith"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          autoFocus
        />
        {receiverName.length > 0 && receiverName.length < 2 && (
          <p className="text-red-500 text-sm">Name must be at least 2 characters</p>
        )}
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={handleBack}
          className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleNext}
          disabled={receiverName.length < 2}
          className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          Next
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  // Step 2: Photos
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Camera className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Upload delivery photos</h3>
        <p className="text-gray-500 text-sm mt-1">Take photos of the delivered items (max 5)</p>
      </div>

      <PhotoUploader
        jobId={jobId}
        photos={photos}
        onPhotosChange={setPhotos}
        maxPhotos={5}
        onUploadingChange={setUploadingPhotos}
      />

      <div className="flex gap-3 pt-4">
        <button
          onClick={() => setStep(1)}
          className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={photos.length === 0 || uploadingPhotos}
          className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {uploadingPhotos ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              Next
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Step 3: Notes
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <FileText className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Delivery notes</h3>
        <p className="text-gray-500 text-sm mt-1">Add any additional information about the delivery</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Notes <span className="text-red-500">*</span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., Left at front door, customer verified items, etc."
          rows={5}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
          autoFocus
        />
        <div className="flex justify-between text-sm">
          {notes.length < 5 ? (
            <p className="text-red-500">Minimum 5 characters required</p>
          ) : (
            <span />
          )}
          <p className="text-gray-400">{notes.length} characters</p>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <h4 className="font-medium text-gray-700">Summary</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p><span className="font-medium">Receiver:</span> {receiverName}</p>
          <p><span className="font-medium">Photos:</span> {photos.length} uploaded</p>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={() => setStep(2)}
          className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={notes.length < 5 || uploadingPhotos}
          className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          Next
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  // Step 4: Confirmation & Review Option
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Mail className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Confirm & Send Email</h3>
        <p className="text-gray-500 text-sm mt-1">Review your options before completing</p>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <h4 className="font-medium text-gray-700">Delivery Summary</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p><span className="font-medium">Receiver:</span> {receiverName}</p>
          <p><span className="font-medium">Photos:</span> {photos.length} uploaded</p>
          <p><span className="font-medium">Notes:</span> {notes.substring(0, 50)}{notes.length > 50 ? '...' : ''}</p>
        </div>
      </div>

      {/* Review Options */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
          <Star className="w-4 h-4" />
          Review Request Options
        </h4>
        
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={doNotSendReview}
            onChange={(e) => setDoNotSendReview(e.target.checked)}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-700">Do not send review request to customer</span>
            <p className="text-xs text-gray-500 mt-0.5">
              If checked, the delivery confirmation email will not include Google and Trustpilot review links.
            </p>
          </div>
        </label>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
        <p className="text-sm text-teal-800">
          <span className="font-medium">📧 Email will be sent to customer</span>
          <br />
          <span className="text-teal-700">
            A delivery confirmation email will be automatically sent to the customer with delivery details
            {doNotSendReview ? ' (without review links).' : ' including photos and review links.'}
          </span>
        </p>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={() => setStep(3)}
          className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || uploadingPhotos}
          className="flex-1 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : uploadingPhotos ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Uploading photos...
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              {existingData?.status === 'delivered' 
                ? (doNotSendReview ? 'Save Changes (No Review)' : 'Save Changes & Send Email')
                : doNotSendReview 
                  ? 'Complete Delivery (No Review)' 
                  : 'Complete & Send Email'
              }
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl p-4">
      {renderProgressBar()}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </div>
  );
};

CompleteDeliveryFlow.displayName = 'CompleteDeliveryFlow';
